<template>
  <ItemListCollectionWrapper
    class="ApplicationItemList__wrapper"
    :collectionModule="$options.ApplicationCollection"
    defaultSortBy="displayLabel"
    :refreshKey="0"
    rowsPerPageLocalStorageKey=""
    selectable
    @selectAll="handleSelectAllEventTagging"
    @selectItem="handleSelectItemEventTagging"
  >
    <template v-slot="slotProps">
      <SeverityModal
        v-if="showDeleteModal"
        isMarkdown
        :notificationMessage="notificationMessageMarkdown"
        :notificationTitle="notificationTitle"
        :numberAffectedProp="numOfAppsToDelete"
        :primaryButtonProps="primaryButtonProps"
        :showModal="showDeleteModal"
        :title="deleteModalTitle"
        useInputNumber
        variant="sev3"
        @close="showDeleteModal = false;"
        @save="handleDeleteSelectedApplications"
      >
        <template v-slot:subtitle>
          <ParagraphBody
            :data-test-id="$testId('subtitle')"
            weight="semibold"
          >
            {{ deleteModalSubtitle }}
          </ParagraphBody>
        </template>
        <template v-slot:list>
          <ParagraphBody>
            Enter the number of applications to be deleted:
          </ParagraphBody>
        </template>
      </SeverityModal>
      <ItemListPagination
        class="ItemListPagination__wrapper"
        :columns="$options.appItemListPaginationColumns"
        :currentPageNum="slotProps.currentPageNum"
        :indexToAddLine="slotProps.indexToAddLine"
        :isLoading="slotProps.loading"
        :itemComponent="$options.ApplicationItemListRow"
        :rowsPerPage="slotProps.rowsPerPage"
        :selectAll="slotProps.selectAll"
        :selectable="slotProps.selectable"
        :showEmptyState="slotProps.showEmptyState"
        :sortBy="slotProps.sortBy"
        :sortDirection="slotProps.sortDirection"
        :tableData="slotProps.tableData"
        :totalCount="slotProps.totalCount"
        @pageChange="slotProps.handlePageChange"
        @select="slotProps.handleSelect"
        @selectAll="slotProps.handleSelectAll"
        @sortColumn="slotProps.handleSortColumn"
      >
        <template v-slot:toolbar>
          <div>
            <div class="header">
              <HeadingSmall
                class="configuredAppsHeading"
                :data-test-id="$testId('configuredAppsHeading')"
              >
                Configured Applications
              </HeadingSmall>
              <div class="dividerLine" />
            </div>
            <ApplicationItemListToolbar
              :reportType="reportType"
              :searchTerm="slotProps.getSearchTerm"
              :shouldFocusSearch="slotProps.shouldFocusSearch"
              @deleteButtonClick="handleDeleteButtonClick"
              @triggerSearch="slotProps.handleEnterSearchTerm"
            />
          </div>
        </template>
        <template v-slot:emptyState>
          <ApplicationItemListNoSearchResults
            :isLoading="slotProps.loading"
            @click="slotProps.clearSearchTerm"
          />
        </template>
      </ItemListPagination>
    </template>
  </ItemListCollectionWrapper>
</template>

<script>
import {
  HeadingSmall,
  ItemListPagination,
  ParagraphBody,
  SeverityModal,
  Types,
} from '@jumpcloud/ui-components';
import { ReportTypes } from 'client/Reports/ReportMetadata';
import { UiEvents } from '@jumpcloud/ui-events-vue';
import { mapActions, mapGetters } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import pluralize from 'pluralize';

import { appItemListPaginationColumns } from 'client/applications/ApplicationItemTable/ApplicationItemTableColumns';
import { eventLocations } from 'client/GrowthEngineering/utilities/GrowthConstants';
import ApplicationItemListNoSearchResults from 'client/components/ItemTable/BodyComponents/ApplicationItemListNoSearchResults.vue';
import ApplicationItemListRow from 'client/components/ItemTable/ApplicationItemListRow.vue';
import ApplicationItemListToolbar from 'client/components/ItemTable/BodyComponents/ApplicationItemListToolbar.vue';
import ItemListCollectionWrapper from 'client/components/ItemList/ItemListCollectionWrapper.vue';

const ApplicationCollection = 'ApplicationCollection';

const { PrimaryButtonProps } = Types;

function getDeleteModalProps(selectedCount) {
  const word = pluralize('Application', selectedCount);
  const wordLowerCase = word.toLowerCase();
  const multi = selectedCount > 1;

  return {
    notificationMessageMarkdown: `Deleting the selected ${wordLowerCase} will permanently remove ${multi ? 'them' : 'it'}.

• You are about to delete ${selectedCount} ${wordLowerCase}

• Users associated will no longer be able to access ${multi ? 'these' : 'this'} ${wordLowerCase} for single sign-on

• Users and attributes will no longer be synced between JumpCloud and ${multi ? 'these' : 'this'} ${wordLowerCase}
`,
    notificationTitle: `Delete ${word} Warning`,
    primaryButtonProps: PrimaryButtonProps({
      text: `Delete ${word}`,
      hasOutline: true,
    }),
    subtitle: `Are you sure you want to delete the selected ${wordLowerCase}?`,
    title: `Delete ${word}`,
  };
}

export default {
  name: 'ApplicationItemList',

  components: {
    ApplicationItemListNoSearchResults,
    ApplicationItemListToolbar,
    HeadingSmall,
    ItemListCollectionWrapper,
    ItemListPagination,
    ParagraphBody,
    SeverityModal,
  },

  data: () => ({
    primaryButtonProps: PrimaryButtonProps({}),
    deleteModalTitle: '',
    deleteModalSubtitle: '',
    notificationMessageMarkdown: '',
    notificationTitle: '',
    numOfAppsToDelete: 0,
    reportType: ReportTypes.userToSso,
    showDeleteModal: false,
  }),

  appItemListPaginationColumns,
  ApplicationCollection,
  ApplicationItemListRow,
  eventTaggingSectionText: 'Applications Item List on Applications/SSO main panel',

  computed: {
    ...mapGetters(ApplicationCollection, {
      selectedApps: 'getSelectedItems',
      selectedAppsCount: 'getSelectedItemsCount',
    }),
  },

  methods: {
    ...mapActions(ApplicationCollection, ['deleteItems']),
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
      'triggerSuccessNotification',
    ]),

    handleDeleteButtonClick() {
      if (this.selectedAppsCount < 1) return;

      const {
        notificationMessageMarkdown,
        notificationTitle,
        primaryButtonProps,
        subtitle,
        title,
      } = getDeleteModalProps(this.selectedAppsCount);

      this.primaryButtonProps = primaryButtonProps;
      this.deleteModalTitle = title;
      this.deleteModalSubtitle = subtitle;
      this.notificationMessageMarkdown = notificationMessageMarkdown;
      this.notificationTitle = notificationTitle;
      this.numOfAppsToDelete = this.selectedAppsCount;

      this.showDeleteModal = true;
    },

    async handleDeleteSelectedApplications() {
      try {
        const { deletedIds, failedTasks } = await this.deleteItems(this.selectedApps);

        if (failedTasks.length) {
          throw new Error();
        }

        this.triggerSuccessNotification({
          message: `${deletedIds.length} ${pluralize('Application', deletedIds.length)} successfully deleted.`,
        });
      } catch (e) {
        this.triggerErrorNotification({
          message: `There was an error deleting the selected ${pluralize('Application', this.selectedApps.length)}`,
        });
      } finally {
        this.showDeleteModal = false;
      }
    },

    handleSelectAllEventTagging(checkedStatus) {
      UiEvents.triggerCheckboxClicked({
        description: 'Select All checkbox from Applications Item List, on Applications/SSO main panel',
        page: eventLocations.ssoPage,
        section: this.$options.eventTaggingSectionText,
        text: checkedStatus ? 'select all' : 'unselect all',
        value: checkedStatus,
      });
    },

    handleSelectItemEventTagging({ checkedStatus }) {
      UiEvents.triggerCheckboxClicked({
        description: 'Toggles the selected status of an individual application',
        page: eventLocations.ssoPage,
        section: this.$options.eventTaggingSectionText,
        value: checkedStatus,
      });
    },
  },
};
</script>

<style scoped>
.ItemListPagination__wrapper >>> .ItemWithLabel,
.ItemListPagination__wrapper >>> .ItemRow {
  max-height: 57px;
}

.ItemListPagination__wrapper >>> .logo-image {
  height: 100%;
}

.header {
  margin-left: var(--jc-spacer-negative);
}

.dividerLine {
  background-color: var(--jc-color-disabled-stroke);
  height: 1px;
  width: 100%;
}

.configuredAppsHeading {
  margin: 20px 0 20px 20px !important;
}
</style>
